import {isDefined} from '../../../../libs/shared/helpers/src/typescript/typeHelpers';
import {CommonSchema} from '../../../models/api/integrationTypesModel';

export type ParameterId = string;

// e.g "^[a-zA-Z]"
type RegexString = string & {_type: 'regex'};

export interface StringParameter {
  id: ParameterId;
  title?: string;
  description?: string;
  public?: boolean;
  collapsable?: boolean;

  type: 'string';
  pattern?: RegexString;
  patternError?: string;
  canUsePreviousResult?: boolean;
  canUseRegex?: boolean;
  multiline?: boolean;
}

export interface NumberParameter {
  id: ParameterId;
  title: string;
  public: boolean;
  collapsable?: boolean;

  type: 'number';
}

interface ArrayParameter {
  id: ParameterId;
  title: string;
  public: boolean;
  collapsable?: boolean;

  type: 'array';
  items: Parameter;
  minItems: number;
  uniqueItems: boolean;
  additionalItems: boolean;
  default?: any[];
}

interface ObjectParameter {
  id: ParameterId;
  title: string;
  public: boolean;
  collapsable?: boolean;

  type: 'object';
  default?: any;
  properties?: any;
}

export type SnoozePresetParameter = ObjectParameter & {format: 'snooze_preset'};

// A string linking to a schema, e.g '#/definitions/messageType'
export type SchemaReference = string & {_type: 'SchemaReference'};
export interface SchemaRefParameter {
  id: ParameterId;
  public: boolean;
  collapsable?: ReadonlyArray<string>;
  type?: string;

  $ref: SchemaReference;
}

// Schema references that are too complex to have generic inputs.
export enum KnownSchemaRefsEnum {
  TIME_RANGE = '#/definitions/timeRange',
  DAYS_OF_THE_WEEK = '#/definitions/daysOfTheWeek',
  DELAY = '#/definitions/delay',
}

export type EnumId = string;

export interface EnumParameter {
  id: ParameterId;
  title: string;
  public: boolean;
  collapsable?: boolean;

  enum: ReadonlyArray<EnumId>;
  default?: EnumId;
  enumTitles: ReadonlyArray<string>;
  enumDescriptions: ReadonlyArray<string>;
}

export interface BooleanParameter {
  id: ParameterId;
  title: string;
  public?: boolean;
  collapsable?: boolean;
  help?: {
    text: string;
    link: string;
  };

  type: 'boolean';
}

type TeammateParameter = NumberParameter & {format: 'teammate'};
type InboxParameter = NumberParameter & {format: 'inbox'};
type TagParameter = NumberParameter & {format: 'tag'};
type ResponseParameter = NumberParameter & {format: 'response'};
type GroupParameter = NumberParameter & {format: 'group'};
type CircleParameter = NumberParameter & {format: 'circle'};
type ChannelParameter = NumberParameter & {format: 'channel'};
type CustomFieldParameter = NumberParameter & {format: 'custom_field'};
type TimezoneParameter = StringParameter & {format: 'timezone'};
type FileExtensionParameter = StringParameter & {format: 'file_extension'};
type ExpressionFieldParameter = NumberParameter & {format: 'expression'; expressionFormat?: string};

export type Parameter =
  | StringParameter
  | NumberParameter
  | ArrayParameter
  | SnoozePresetParameter
  | SchemaRefParameter
  | EnumParameter
  | BooleanParameter
  | TeammateParameter
  | InboxParameter
  | TagParameter
  | ResponseParameter
  | GroupParameter
  | ChannelParameter
  | CustomFieldParameter
  | TimezoneParameter
  | FileExtensionParameter
  | ExpressionFieldParameter
  | CircleParameter;

export const Parameter = {
  isString(r: Parameter): r is StringParameter {
    return (r as StringParameter).type === 'string';
  },

  isNumber(r: Parameter): r is NumberParameter {
    return (r as NumberParameter).type === 'number';
  },

  isGroup(r: Parameter): r is GroupParameter {
    return (r as GroupParameter).format === 'group';
  },

  isChannel(r: Parameter): r is ChannelParameter {
    return (r as ChannelParameter).format === 'channel';
  },

  isTag(r: Parameter): r is TagParameter {
    return (r as TagParameter).format === 'tag';
  },

  isInbox(r: Parameter): r is InboxParameter {
    return (r as InboxParameter).format === 'inbox';
  },

  isTeammate(r: Parameter): r is TeammateParameter {
    return (r as TeammateParameter).format === 'teammate';
  },

  isResponse(r: Parameter): r is ResponseParameter {
    return (r as ResponseParameter).format === 'response';
  },

  isEnum(r: Parameter | CommonSchema): r is EnumParameter {
    return Array.isArray((r as EnumParameter).enum);
  },

  isSchemaRef(r: Parameter): r is SchemaRefParameter {
    return (r as SchemaRefParameter).$ref !== undefined;
  },

  isCustomField(r: Parameter): r is CustomFieldParameter {
    return (r as CustomFieldParameter).format === 'custom_field';
  },

  isExpression(r: Parameter): r is ExpressionFieldParameter {
    return (r as ExpressionFieldParameter).format === 'expression';
  },

  isArray(r: Parameter): r is ArrayParameter {
    return (r as ArrayParameter).type === 'array';
  },

  isTimezone(r: Parameter): r is TimezoneParameter {
    return (r as TimezoneParameter).format === 'timezone';
  },

  isFileExtension(r: Parameter): r is FileExtensionParameter {
    return (r as FileExtensionParameter).format === 'file_extension';
  },

  isBoolean(r: Parameter): r is BooleanParameter {
    return (r as BooleanParameter).type === 'boolean';
  },

  isSnoozePreset(r: Parameter): r is SnoozePresetParameter {
    return (r as SnoozePresetParameter).format === 'snooze_preset';
  },

  isPublic(r: Parameter): boolean {
    return !isDefined(r.public) || r.public;
  },

  isCircle(r: Parameter): r is CircleParameter {
    return (r as CircleParameter).format === 'circle';
  },
};
